import { useEffect } from "react";
import { Editor, TLShape, TLShapeId } from "@tldraw/tldraw";
import { performBooleanOperation, calculateRelativeClipPath, getOperationLabel } from "../utils/booleanOperations";

/**
 * Hook to listen for changes in mask group shapes and recalculate clip-paths in real-time
 * This enables dynamic masking where the mask updates as you edit the shapes
 * Only active when shapes are visible (in edit mode)
 */
export function useMaskGroupListener(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;

    let debounceTimer: NodeJS.Timeout | null = null;

    const unsubscribe = editor.store.listen(
      ({ changes }) => {
        if (!changes.updated) return;

        // Clear previous debounce
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }

        // Debounce recalculations to avoid performance issues
        debounceTimer = setTimeout(() => {
          const affectedGroupIds = new Set<string>();

          // Check each updated shape
          for (const [_id, [fromRecord, toRecord]] of Object.entries(changes.updated)) {
            const shape = toRecord as TLShape;

            // Skip preview shapes (they should not trigger recalculation)
            if (shape.meta?.isPreviewShape) {
              continue;
            }

            const fromShape = fromRecord as TLShape;
            const opacityChanged = fromShape.opacity !== shape.opacity;
            const positionOrSizeChanged =
              fromShape.x !== shape.x ||
              fromShape.y !== shape.y ||
              (fromShape.props as any)?.w !== (shape.props as any)?.w ||
              (fromShape.props as any)?.h !== (shape.props as any)?.h;

            // Detect change in mask operation (when user changes mask type)
            const maskOperationChanged =
              shape.meta?.isMaskGroup && fromShape.meta?.maskOperation !== shape.meta?.maskOperation;

            // If mask operation changed, handle preview cleanup/creation
            if (maskOperationChanged) {
              const newOp = shape.meta?.maskOperation as string;
              const oldOp = fromShape.meta?.maskOperation as string;
              const needsPreviewNow = newOp === "union" || newOp === "exclude";
              const hadPreviewBefore = oldOp === "union" || oldOp === "exclude";

              // If switching away from preview operations, clean up previews and restore image opacity
              if (hadPreviewBefore && !needsPreviewNow) {
                const allShapes = editor.getCurrentPageShapes();
                const previewsForGroup = allShapes.filter(
                  (s) => s.meta?.isPreviewShape && s.meta?.previewForGroup === shape.id
                );
                if (previewsForGroup.length > 0) {
                  previewsForGroup.forEach((preview) => {
                    editor.updateShape({ ...preview, isLocked: false });
                  });
                  editor.deleteShapes(previewsForGroup.map((p) => p.id));
                }

                // Restore image opacity (was hidden for preview)
                const children = editor
                  .getSortedChildIdsForParent(shape.id as TLShapeId)
                  .map((id) => editor.getShape(id))
                  .filter(Boolean);
                children.forEach((child: any) => {
                  if (child.type === "customImage" && child.meta?.isInMaskGroup) {
                    editor.updateShape({ ...child, opacity: 1 });
                  }
                });
              }

              affectedGroupIds.add(shape.id);
              continue;
            }

            // If mask group itself moved, recalc its preview
            if (shape.meta?.isMaskGroup && positionOrSizeChanged) {
              affectedGroupIds.add(shape.id);
              continue;
            }

            // Only recalc if child moved (not just opacity)
            if (
              shape.meta?.isInMaskGroup &&
              shape.parentId &&
              shape.parentId !== "page:page" &&
              !opacityChanged && // Skip if we're just hiding/showing
              positionOrSizeChanged // Only if position/size actually changed
            ) {
              affectedGroupIds.add(shape.parentId);
            }
          }

          // Recalculate each affected mask group
          affectedGroupIds.forEach((groupId) => {
            recalculateMaskGroup(groupId, editor);
          });
        }, 16); // Faster debounce for smoother updates (60fps = ~16ms)
      },
      { source: "user", scope: "document" }
    );

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      unsubscribe();
    };
  }, [editor]);
}

/**
 * Recalculates the clip-path for all shapes in a mask group
 * Only runs when shapes are in edit mode (visible)
 */
function recalculateMaskGroup(groupId: string, editor: Editor) {
  try {
    const group = editor.getShape(groupId as TLShapeId);
    if (!group || group.type !== "group" || !group.meta?.isMaskGroup) {
      return;
    }

    const operation = group.meta.maskOperation as "union" | "subtract" | "intersect" | "exclude";
    if (!operation) {
      console.warn("Mask group missing operation metadata");
      return;
    }

    // Get all children of the group
    const childIds = editor.getSortedChildIdsForParent(groupId as TLShapeId);
    const children = childIds.map((id) => editor.getShape(id)).filter(Boolean) as TLShape[];

    // Get original shapes (not mask result shapes)
    const originalShapes = children.filter((shape) => shape.meta?.isInMaskGroup);

    if (originalShapes.length < 2) {
      console.warn(`Mask group has ${originalShapes.length} shape(s), cannot perform masking`);
      return;
    }

    // Perform the boolean operation
    const result = performBooleanOperation(originalShapes, operation, editor);

    if (!result) {
      console.error("Failed to recalculate mask operation");
      return;
    }

    // Update clip-path for each original shape (only if they're visible)
    originalShapes.forEach((shape) => {
      if (shape.opacity > 0) {
        // Only update visible shapes
        const relativeClipPath = calculateRelativeClipPath(result.svgPath, shape, editor);

        editor.updateShape({
          ...shape,
          meta: {
            ...shape.meta,
            maskClipPath: relativeClipPath,
          },
        });
      }
    });

    // Update preview shape if it exists (for union/exclude with images)
    const allShapes = editor.getCurrentPageShapes();
    const previewShape = allShapes.find((s) => s.meta?.isPreviewShape && s.meta?.previewForGroup === groupId);

    if (previewShape && previewShape.type === "customImage") {
      const label =
        (previewShape.meta?.booleanLabel as string | undefined) ||
        (group.meta?.booleanLabel as string | undefined) ||
        getOperationLabel(operation);
      const previewName = (previewShape.meta?.name as string | undefined) || `${label} Preview`;

      // Calculate clip-path relative to result bounds (for projection)
      const pathRegex = /([ML])\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)/g;
      const previewClipPath = result.svgPath.replace(pathRegex, (match, command, x, y) => {
        const absX = parseFloat(x);
        const absY = parseFloat(y);
        const relX = absX - result.bounds.minX;
        const relY = absY - result.bounds.minY;
        return `${command} ${relX} ${relY}`;
      });

      // 🔥 Force remount of preview shape to ensure CustomImageUtil re-renders
      const nextTransparency = (previewShape.props as any).transparency === 1 ? 0.99999 : 1;
      const nextOpacity = previewShape.opacity === 1 ? 0.99999 : 1;

      editor.batch(() => {
        // delete existing preview
        editor.deleteShapes([previewShape.id]);

        // recreate with same id so listeners keep tracking it
        editor.createShape({
          id: previewShape.id,
          type: "customImage",
          x: result.bounds.minX,
          y: result.bounds.minY,
          opacity: nextOpacity,
          isLocked: true,
          props: {
            ...previewShape.props,
            w: result.bounds.width,
            h: result.bounds.height,
            transparency: nextTransparency,
          } as any,
          meta: {
            ...previewShape.meta,
            maskClipPath: previewClipPath,
            updateTimestamp: Date.now(),
            booleanLabel: label,
            name: previewName,
          },
        });
      });
    }
  } catch (error) {
    console.error("Error recalculating mask group:", error);
  }
}
